import URI from 'jsuri';
import Q from 'q';

import API from 'js/lib/api';

import type { Partner } from 'bundles/admin/constants/types';
import type AuthoringCourse from 'bundles/author-common/models/AuthoringCourse';
import type { CourseMetadata } from 'bundles/author-common/types/CourseMetadata';
import type { AccessibilitySettingsWithMetaData } from 'bundles/authoring/asset-library/components/AccessibilitySettingsModal';
import type { AuthoringCourseContext } from 'bundles/authoring/common/types/authoringCourseContexts';
import type { AuthoringCourseWithMetadata } from 'bundles/naptimejs/resources/__generated__/AuthoringCoursesV2';
import type { PartnerTypeMetadataV1Connection } from 'bundles/naptimejs/resources/__generated__/PartnerTypeMetadataV1';
import type { PartnerTypeMetadata } from 'bundles/teach-course/stores/TeachAppStore';

const OpenCourseMembershipsAPI = API('/api/openCourseMemberships.v1', {
  type: 'rest',
});
const AuthoringCoursesAPI = API('/api/authoringCourses.v2', { type: 'rest' });
const PartnersAPI = API('/api/partners.v1', { type: 'rest' });
const authoringCourseContextAPI = API('/api/authoringCourseContexts.v1', { type: 'rest' });
const partnerTypeMetadataAPI = API('/api/partnerTypeMetadata.v1', { type: 'rest' });

/**
 * @deprecated - this should be moved to graphql.
 */
const AuthoringCourseAPIUtils = {
  /**
   * Gets AuthoringCourse from courseId by calling authoringCourses.v2
   */
  async getCourse(courseId: string): Promise<{ course: AuthoringCourse; metadata: CourseMetadata }> {
    const payload = await AuthoringCoursesAPI.get(courseId);
    const authoringCourse = payload.elements[0];
    authoringCourse.course.id = authoringCourse.id;
    const partners = await AuthoringCourseAPIUtils.getPartners(authoringCourse.course.partnerIds);
    authoringCourse.course.partners = partners;
    return authoringCourse;
  },

  /**
   * Gets AuthoringCourse from courseSlug by calling authoringCourses.v2
   */
  getCourseBySlug(slug: string): Q.Promise<{ course: AuthoringCourse; metadata: CourseMetadata }> {
    const uri = new URI().addQueryParam('q', 'bySlug').addQueryParam('slug', slug);

    return Q(AuthoringCoursesAPI.get(uri.toString())).then((payload) => {
      // assign course id to course object
      const authoringCourse = payload.elements[0];
      authoringCourse.course.id = authoringCourse.id;
      return AuthoringCourseAPIUtils.getPartners(authoringCourse.course.partnerIds).then((partners) => {
        authoringCourse.course.partners = partners;
        return authoringCourse;
      });
    });
  },

  /**
   * Gets partner information for the current course
   */
  getPartners(partnerIds: Array<number>): Q.Promise<Array<Partner>> {
    const uri = new URI()
      .addQueryParam('ids', partnerIds.join(','))
      .addQueryParam('fields', 'squareLogo,rectangularLogo');
    return Q(PartnersAPI.get(uri.toString())).then((payload) => payload.elements);
  },

  /**
   * Gets membership information for current user in courseId
   * A 404 error corresponds to the default "Browser Role"
   * Any other errors will get thrown.
   */
  getMembership(courseId: string, userId: number | string): Q.Promise<string> {
    return Q(OpenCourseMembershipsAPI.get(userId + '~' + courseId))
      .then((payload) => (payload.elements ? payload.elements[0].courseRole : 'BROWSER'))
      .catch((err) => {
        if (err.status === 404) {
          return 'BROWSER';
        } else {
          throw err;
        }
      });
  },

  /**
   * Returns the authoring course contexts given a courseId.
   * @param {string} courseId
   */
  getCourseContexts(
    courseId: string
  ): Q.Promise<{ contexts: Array<AuthoringCourseContext | []>; contextIds: Array<string> }> {
    const uri = new URI(courseId).addQueryParam('fields', 'contexts,contextIds');
    return Q(authoringCourseContextAPI.get(uri.toString())).then((payload) =>
      payload.elements ? payload.elements[0] : []
    );
  },

  /**
   * Returns a boolean on whether the partnerId is of partnerType `PRIVATE_AUTHORING`
   * NOTE: the auth is scoped to partner admin permissions. For course-level, use the finder below `getPartnerTypeMetadata`.
   * @param {number} partnerId
   */
  getPrivateAuthoringPartner({ partnerId }: { partnerId: number | string | null }): Q.Promise<boolean | void> {
    if (!partnerId) {
      return Q();
    }

    return Q(
      partnerTypeMetadataAPI.get(`${partnerId}`).then((response: PartnerTypeMetadataV1Connection) => {
        const metadata = response.elements ? response.elements[0] : null;

        if (!metadata) {
          return false;
        }

        return metadata.isPrivateAuthoringPartner;
      })
    );
  },

  /**
   * returns partner metadata for a course's parnterIds. Note that this call has to be made on CSR,
   * since one of the underlying API uses POST which will fail on SSR.
   * @param {string} courseId
   */
  getPartnerTypeMetadata({ courseId }: { courseId: string | null }): Q.Promise<PartnerTypeMetadata | void> {
    if (!courseId) {
      return Q();
    }

    const uri = new URI().addQueryParam('q', 'byCourse').addQueryParam('courseId', courseId);

    return Q(partnerTypeMetadataAPI.get(uri.toString())).then((response) => {
      const metadata = response.elements ? response.elements[0] : null;

      if (!metadata) {
        return {
          isC4CPartner: false,
          isPrivateAuthoringPartner: false,
        };
      }

      return {
        isC4CPartner: metadata.isEnterpriseClientPartner,
        isPrivateAuthoringPartner: metadata.isPrivateAuthoringPartner,
      };
    });
  },

  /**
   * Updates the accessibility settings of a course given a courseId and body by calling authoringCourses.v2
   */
  updateCourseAccessibilitySettings(
    courseId: string,
    accessibilitySettingsWithMetaData: AccessibilitySettingsWithMetaData
  ): Q.Promise<AuthoringCourseWithMetadata> {
    return Q(
      AuthoringCoursesAPI.post('?action=assetLibraryAccessibilitySettingsUpdate&id=' + courseId, {
        data: accessibilitySettingsWithMetaData,
      })
    )
      .then((payload) => payload)
      .catch((err) => err);
  },
};

export default AuthoringCourseAPIUtils;

export const {
  getCourse,
  getCourseBySlug,
  getPartners,
  getMembership,
  getPrivateAuthoringPartner,
  getPartnerTypeMetadata,
  getCourseContexts,
  updateCourseAccessibilitySettings,
} = AuthoringCourseAPIUtils;
